<template>
  <q-dialog v-model="openSync">
    <q-card v-if="hasUser" style="width: 50rem; max-width: 80vw; height: 90%">
      <q-card-section>
        <h6 class="text-primary text-center q-mb-md">Transporter Management Detail</h6>
      </q-card-section>

      <q-card-section>
        <h6>
          Registered Account:
          <span class="text-weight-light">{{ transporter.user.email }}</span>
        </h6>
      </q-card-section>

      <!-- General Info -->
      <q-card-section>
        <div class="container overflow-auto">
          <div class="bg-gray row q-col-gutter-md">
            <div class="col-6 col-md-4 flex items-center">
              <span class="text-muted q-mr-md">Portrait:</span>
              <q-avatar rounded size="4rem">
                <img :src="userProfilePicture" alt="transporter profile picture" />
              </q-avatar>
              <div class="self-end">
                <q-btn flat round color="primary" icon="cloud_upload" @click="() => $refs.picture.click()" />
                <input
                  ref="picture"
                  type="file"
                  hidden
                  name="logo"
                  accept="image/jpeg, image/png"
                  @change="handleFileChange"
                />
              </div>
            </div>

            <div class="col-6 col-md-8">
              <div class="row q-mt-sm q-col-gutter-md items-center">
                <div class="col-12 col-md-6">
                  <q-input v-model="transporter.operationCity.name" class="q-mb-none" dense label="Port of Operation" />
                </div>

                <div class="col-12 col-md-6">
                  <div class="q-ml-lg">
                    <p class="text-muted q-ma-none">Status:</p>
                    <div class="flex items-center">
                      <template v-if="transporter.user.isActive === true">
                        <span class="text-body1 text-positive q-mr-md">Active</span>
                        <q-btn dense flat color="negative" label="Suspend user" @click="$emit('suspendUser')" />
                      </template>

                      <template v-else>
                        <span class="text-body1 text-negative q-mr-md">Suspended</span>
                        <q-btn dense flat color="positive" label="Enable user" @click="$emit('enableUser')" />
                      </template>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-md-6">
                  <q-input
                    v-model="transporter.abn"
                    dense
                    label="ABN Number"
                    bottom-slots
                    no-error-icon
                    :error="$v.transporter.abn.$dirty && $v.transporter.abn.$invalid"
                  >
                    <template #error>
                      <span v-if="!$v.transporter.abn.required"> ABN is required </span>

                      <span v-else-if="!$v.transporter.abn.minLength">
                        Valid ABN must have at least 11 characters
                      </span>

                      <span v-else-if="!$v.transporter.abn.numeric"> ABN must contain only numeric characters </span>
                    </template>
                  </q-input>
                </div>

                <div class="col-12 col-md-6 q-pr-md">
                  <q-btn
                    class="full-width"
                    unelevated
                    color="positive"
                    :disabled="!allowVerify"
                    :icon-right="isVerified ? 'check_circle' : undefined"
                    :flat="isVerified"
                    :label="isVerified ? 'Verified' : 'Verify transporter'"
                    @click="verifyDialog = true"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </q-card-section>

      <q-card-section v-if="transporter.verification" tag="section">
        <h6 class="q-mb-md">Verification Documents:</h6>

        <div class="bg-gray q-pr-md q-pb-md q-ml-none row q-col-gutter-md">
          <div class="col-6">
            <a target="_blank" rel="noopener noreferrer" :href="transporter.verification.registrationUrl">
              <span>Registration </span>
              <q-icon name="link" />
              <q-tooltip v-if="files.registration.size" anchor="top middle">
                {{ files.registration.size + files.registration.append }}
              </q-tooltip>
            </a>
          </div>

          <div class="col-6">
            <a target="_blank" rel="noopener noreferrer" :href="transporter.verification.vbsUrl">
              <span>VBS </span>
              <q-icon name="link" />
              <q-tooltip v-if="files.vbs.size" anchor="top middle">
                {{ files.vbs.size + files.vbs.append }}
              </q-tooltip>
            </a>
          </div>
        </div>
      </q-card-section>

      <!-- Business Details -->
      <q-card-section>
        <EditBusinessDetail :transporter.sync="transporter" :v="$v" />
      </q-card-section>

      <!-- Company Info -->
      <q-card-section>
        <EditDialogCompanyInfo
          :year-established.sync="transporter.yearEstablished"
          :rep-display-name.sync="transporter.repDisplayName"
          :employees.sync="transporter.employees"
          :employed-drivers.sync="transporter.employedDrivers"
        />
      </q-card-section>

      <!-- Company Representative -->
      <q-card-section>
        <EditRepresentative :transporter.sync="transporter" :v="$v" />
      </q-card-section>

      <!-- Service Options -->
      <q-card-section>
        <EditServiceOptions :transporter.sync="transporter" :v="$v" />
      </q-card-section>

      <q-card-section class="q-mt-lg">
        <div class="bg-gray q-pr-md q-pb-md q-ml-none row q-col-gutter-md items-center">
          <div class="col-12 col-md-3">
            <q-checkbox v-model="transporter.repConsent" label="Allow marketing promotions" />
          </div>

          <div class="col-12 col-md-3">
            <q-checkbox v-model="isVerifiedEmail" label="Verified Email" disable />
          </div>

          <div class="col-12 col-md-3">
            <q-checkbox v-model="isVerifiedPhone" label="Verified Phone" disable />
          </div>

          <div class="col-12 col-md-3">
            <span>Registration IP: {{ transporter.user.ip }}</span>
          </div>
        </div>
      </q-card-section>

      <q-card-section>
        <p>
          <strong>Admin Notes:</strong>
        </p>
        <notes v-if="transporter.user" :user-id="transporter.user.id" />
      </q-card-section>

      <q-card-actions align="right">
        <q-btn v-close-popup flat label="close" />
        <q-btn flat color="primary" label="save" @click="hasChanged ? updateTransporter(false) : (openSync = false)" />
        <q-btn
          flat
          color="primary"
          label="save and exit"
          @click="hasChanged ? (saveDialog = true) : (openSync = false)"
        />
      </q-card-actions>
    </q-card>

    <VerifyUserDialog
      :is-open.sync="verifyDialog"
      :transporter-id="transporter.id"
      @verify-success="verifySuccess"
      @verify-fail="verifyFail"
    />

    <!--Confirm upload image-->
    <q-dialog :value="!!tempFile" persistent>
      <q-card>
        <q-card-section>
          <h6 class="text-primary">Upload Confirm</h6>
          <p>Are you sure you want to upload this photo?</p>
          <q-avatar size="5rem">
            <img alt="user avatar" :src="imagePreview" />
          </q-avatar>
          <sub class="text-muted block">Size Limit: 100*100px</sub>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="cancel" @click="resetPictureInput" />
          <q-btn color="primary" label="Save" @click="changePhoto" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="saveDialog">
      <q-card>
        <q-card-section>
          <h5 class="text-primary text-weight-bold q-my-sm">Save Confirm</h5>
          <p>Are you sure you want to update this transporter information?</p>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="cancel" />
          <q-btn v-close-popup color="primary" label="Save" @click="updateTransporter()" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-dialog>
</template>

<script>
import { required, requiredIf, numeric, minLength } from 'vuelidate/lib/validators';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import API from '@/apis';
import { getSourceFromAsset, getErrorMessage } from '@/utils';
import EditDialogCompanyInfo from '@/components/EditDialogCompanyInfo.vue';
import uploadFile from '@/utils/uploadFile';
import VerifyUserDialog from './VerifyUserDialog.vue';
import EditBusinessDetail from './EditBusinessDetail.vue';
import EditRepresentative from './EditRepresentative.vue';
import EditServiceOptions from './EditServiceOptions.vue';
import Notes from './Notes.vue';

export default {
  components: {
    Notes,
    EditDialogCompanyInfo,
    VerifyUserDialog,
    EditBusinessDetail,
    EditRepresentative,
    EditServiceOptions,
  },
  props: {
    open: {
      required: true,
      type: Boolean,
    },
    selectedTransporter: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      transporter: {},
      tempFile: null,
      verifyDialog: false,
      saveDialog: false,
      error: null,
      isLoading: false,
      files: {
        registration: {},
        vbs: {},
      },
    };
  },

  computed: {
    openSync: {
      get() {
        return this.open;
      },

      set(value) {
        if (!value) {
          this.$emit('checkTransporterChange', this.hasChanged);
        }
        this.$emit('update:open', value);
      },
    },
    hasUser() {
      return Object.keys(this.transporter).length;
    },
    imagePreview() {
      return this.tempFile ? URL.createObjectURL(this.tempFile) : null;
    },
    userProfilePicture() {
      return this.transporter.companyLogo ? this.transporter.companyLogo : getSourceFromAsset('icon_Personal.png');
    },
    hasChanged() {
      return !isEqual(this.transporter, this.selectedTransporter);
    },
    isVerified() {
      return this.transporter.status === 'verified';
    },
    allowVerify() {
      return this.transporter.status === 'pending';
    },
    isVerifiedPhone() {
      return get(this.transporter, 'user.verifiedPhone.isVerified', false);
    },
    isVerifiedEmail() {
      return get(this.transporter, 'user.verifiedEmail', false);
    },
  },

  async created() {
    const driver = this.selectedTransporter;

    this.transporter = {
      ...driver,
      companyAddress:
        driver.companyAddress && driver.companyAddress.length
          ? driver.companyAddress.map((c) => ({ s: c }))
          : [{ s: '' }],
      operationalAddress:
        driver.operationalAddress && driver.operationalAddress.length
          ? driver.operationalAddress.map((c) => ({ s: c }))
          : [{ s: '' }],
      mailingAddress: driver.mailingAddress,
    };
    this.files.registration = await this.getFileInfo(driver.verification?.registrationUrl);
    this.files.vbs = await this.getFileInfo(driver.verification?.vbsUrl);
  },

  methods: {
    resetPictureInput() {
      this.$refs.picture.value = null;
      this.tempFile = null;
    },
    handleFileChange(event) {
      const [file] = event.target.files;
      this.tempFile = file;
    },
    verifySuccess() {
      this.transporter.status = 'verified';
      this.verifyDialog = false;
      this.$q.notify({ message: 'Transporter successfully verified!', type: 'positive' });
    },
    verifyFail() {
      this.$q.notify({ message: 'Email is invalid!', type: 'negative' });
    },
    async updateTransporter(closeDialog = true) {
      this.$v.$reset();
      this.$v.$touch();

      if (this.$v.$invalid) return;

      if (!this.hasChanged) {
        this.openSync = false;
        return;
      }

      this.isLoading = true;

      try {
        const data = {
          ...this.transporter,
          companyAddress: this.transporter.companyAddress.filter((c) => c.s.length > 0).map((c) => c.s),
          operationalAddress: this.transporter.operationalAddress.filter((c) => c.s.length > 0).map((c) => c.s),
          mailingAddress: this.transporter.mailingAddress,
        };

        await this.$axios.request(API.admin.updateTransporter(this.transporter.id, data));

        this.$q.notify({ message: 'Transporter updated!', type: 'positive' });

        if (closeDialog) {
          this.openSync = false;
        }
      } catch (error) {
        this.$q.notify({ message: getErrorMessage(error), type: 'negative' });
      } finally {
        this.isLoading = false;
      }
    },
    async changePhoto() {
      if (!this.tempFile || !this.transporter) {
        return;
      }
      const userId = this.transporter.id;
      const url = await uploadFile(this.tempFile, 'image');
      try {
        await this.$axios.patch(`/admin/transporter/${userId}/company-logo`, {
          url,
        });
        this.transporter.companyLogo = url;
        this.resetPictureInput();
        this.$q.notify({ message: 'Photo successfully updated!', type: 'positive' });
      } catch (error) {
        this.$q.notify({ message: getErrorMessage(error), type: 'negative' });
      }
    },
    async getFileInfo(url = '') {
      const [filename] = url.split('/').slice(-1);
      if (!filename) {
        return {};
      }
      const { data } = await this.$axios.request(API.file.size(filename));
      return data;
    },
  },

  validations: {
    transporter: {
      operationCity: {},
      abn: {
        required,
        minLength: minLength(11),
        numeric,
      },
      companyName: {
        required,
      },
      tradingName: {
        required,
      },
      mainOfficePhone: {
        required,
        numeric,
      },
      repFirstName: {
        required,
      },
      repLastName: {
        required,
      },
      repPhone: {
        required,
        numeric,
      },
      companyAddress: {
        $each: {
          s: {
            // eslint-disable-next-line
            required: requiredIf(function () {
              return this.transporter.companyAddress[0].s !== '';
            }),
          },
        },
      },
      operationalAddress: {
        $each: {
          s: {
            // eslint-disable-next-line
            required: requiredIf(function () {
              return this.transporter.operationalAddress[0].s !== '';
            }),
          },
        },
      },
      mailingAddress: {
        required,
      },
      serviceOptions: {
        containerTypes: {
          // eslint-disable-next-line
          required: requiredIf(function () {
            return this.transporter.serviceOptions;
          }),
        },
        truckTypes: {
          // eslint-disable-next-line
          required: requiredIf(function () {
            return this.transporter.serviceOptions;
          }),
        },
        deliveryTypes: {
          // eslint-disable-next-line
          required: requiredIf(function () {
            return this.transporter.serviceOptions;
          }),
        },
      },
    },
  },
};
</script>
